/**
 * Non-modal banner that surfaces a currently-running bulk revalidation job
 * above the records list. Polls `GET /revalidate/current` via
 * {@link useCurrentRevalidateJob} — when nothing is running, the banner is
 * collapsed (kept in the DOM for screen-reader continuity but `show={false}`).
 *
 * The banner intentionally does NOT block the rest of the UI. Users can keep
 * browsing, filter, open detail panes; the banner just updates in place.
 * Once the job reaches a terminal state we briefly switch to a success/error
 * message (so the user knows the run is finished) and then the next idle
 * tick from the polling hook lets it collapse.
 *
 * Re-uses {@link useCurrentRevalidateJob} directly rather than reading from
 * a parent — this way the banner remains self-contained and the parent
 * list view only has to render `<RevalidationProgressBanner />` once.
 */
import React, { useEffect, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQueryClient } from 'react-query';
import { MessageBanner } from '@folio/stripes/components';
import { useCurrentRevalidateJob } from '../../hooks/useRevalidateJob';
import { TERMINAL_JOB_STATES } from '../../types/validation';
var TERMINAL_DISPLAY_MS = 6000;
var variantToType = function (variant) {
    switch (variant) {
        case 'success':
            return 'success';
        case 'error':
            return 'error';
        default:
            return 'warning';
    }
};
var RevalidationProgressBanner = function () {
    var _a;
    var intl = useIntl();
    var queryClient = useQueryClient();
    var job = useCurrentRevalidateJob().data;
    // Once a terminal state appears we want the success/error variant to stay
    // visible for a brief window even after the polling hook stops returning
    // it. We hold onto the last terminal job in a ref and clear it on a timer.
    var _b = React.useState(null), terminalJob = _b[0], setTerminalJob = _b[1];
    var timerRef = useRef(null);
    useEffect(function () {
        if (job && TERMINAL_JOB_STATES.has(job.state)) {
            setTerminalJob(job);
            // Refresh the records list so updated validation badges show up.
            // We invalidate broadly — any list query keyed off record fetches will
            // re-fire; the badges hang off RecordSummary.
            queryClient.invalidateQueries(['records']);
            queryClient.invalidateQueries(['record']);
            if (timerRef.current)
                clearTimeout(timerRef.current);
            timerRef.current = setTimeout(function () { return setTerminalJob(null); }, TERMINAL_DISPLAY_MS);
        }
        return function () {
            if (timerRef.current)
                clearTimeout(timerRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [job === null || job === void 0 ? void 0 : job.jobId, job === null || job === void 0 ? void 0 : job.state]);
    // Decide which thing to render:
    //  - active job (QUEUED / RUNNING) → progress variant
    //  - terminal job we still want to show → success or error variant
    //  - otherwise nothing
    var activeJob = job && !TERMINAL_JOB_STATES.has(job.state) ? job : null;
    var displayJob = (_a = activeJob !== null && activeJob !== void 0 ? activeJob : terminalJob) !== null && _a !== void 0 ? _a : null;
    var variant = 'progress';
    if (displayJob && TERMINAL_JOB_STATES.has(displayJob.state)) {
        variant = displayJob.state === 'COMPLETED' ? 'success' : 'error';
    }
    // Pct on the progress variant — guard against division by zero before total
    // is computed (job is QUEUED, scanning has not started).
    var pct = displayJob && displayJob.total > 0
        ? Math.min(100, Math.round((displayJob.processed / displayJob.total) * 100))
        : 0;
    var scopeLabel = displayJob
        ? intl.formatMessage({
            id: "ui-czna-authorities.listActions.revalidate.scope.".concat(displayJob.scope),
        })
        : '';
    var renderBody = function () {
        var _a;
        if (!displayJob)
            return null;
        if (variant === 'progress') {
            return (React.createElement("span", { "data-test-revalidation-progress": true },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.listActions.revalidate.banner.progress", values: {
                        scope: scopeLabel,
                        processed: displayJob.processed,
                        total: displayJob.total || '?',
                        pct: pct,
                    } })));
        }
        if (variant === 'success') {
            return (React.createElement("span", { "data-test-revalidation-completed": true },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.listActions.revalidate.banner.completed", values: {
                        scope: scopeLabel,
                        processed: displayJob.processed,
                        errors: displayJob.errorsFound,
                        warnings: displayJob.warningsFound,
                    } })));
        }
        return (React.createElement("span", { "data-test-revalidation-failed": true },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.listActions.revalidate.banner.failed", values: {
                    scope: scopeLabel,
                    error: (_a = displayJob.errorMessage) !== null && _a !== void 0 ? _a : '—',
                } })));
    };
    return (React.createElement(MessageBanner, { type: variantToType(variant), show: !!displayJob, "data-test-revalidation-banner": true }, renderBody()));
};
export default RevalidationProgressBanner;
